import { CONFIG_ELEMENTS, type SimulatorArea } from '@vedalib/editor/lib/elements'
import { isEmptyRtValue, rtValueToText } from '@vedalib/rich-text'
import { useCallback, useEffect, useRef, useState } from 'react'

import DropdownCourse from 'components/Editor/EditorElements/courseKit/DropDownCourse'
import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import ScreenReaderOnlyText from 'components/Editor/styled/ScreenReaderOnlyText'
import { Tooltip } from 'components/uiKit/Dropdown'
import { AppMode, DeviceMode, PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getBlockId, getIsInteractiveMode } from 'services/Store/Project/selectors'
import { t } from 'services/Translation'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'

import AreaToolbar from '../AreaToolbar'
import HintContent from '../HintContent/HintContent'
import HintToolbar from '../HintToolbar'
import { useSimulatorContext } from '../SimulatorContext'
import LinkedIcon from '../assets/linked.svg?react'
import AreaContainerStyled from '../styled/AreaContainerStyled'
import AreaStyled from '../styled/AreaStyled'
import * as s from './Area.module.scss'

const ANIMATIONS = {
  show: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: 'easeInOut' } },
  hide: { opacity: 0, scale: 0.9, transition: { duration: 0.3, ease: 'easeInOut' } },
}

interface IAreaProps {
  children?: React.ReactNode
  area: SimulatorArea
  selected: boolean
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void
  enableDelete?: boolean
  currentItemId: string
  elementId: string
  container?: HTMLElement | null
}

const Area: React.FC<IAreaProps> = ({
  children,
  area,
  selected,
  onMouseDown,
  enableDelete,
  currentItemId,
  elementId,
  container,
}) => {
  const styles = useSimulatorContext((c) => c.styles)
  const value = useSimulatorContext((c) => c.value)
  const mode = useSimulatorContext((c) => c.mode)
  const showHighlight = useSimulatorContext((c) => c.showHighlight)
  const onDeleteArea = useSimulatorContext((c) => c.onDeleteArea)
  const setState = useSimulatorContext((c) => c.setState)
  const state = useSimulatorContext((c) => c.state)
  const validateFields = useSimulatorContext((c) => c.validateFields)
  const blockUuid = useProjectContext(getBlockId) || ''

  const areaRef = useRef<HTMLDivElement>(null)
  const isPreview = useProjectContext(getIsInteractiveMode)
  const isDemoMode = mode?.previewMode === PreviewMode.demo
  const isSkeleton = mode.previewMode === PreviewMode.skeleton
  const isFill = mode.previewMode === PreviewMode.editor && mode.editorMode === AppMode.fill
  const isPro = mode.editorMode === AppMode.pro
  const isMobile = mode.deviceMode === DeviceMode.mobile

  const [isDragging, setIsDragging] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const hintEmptyPreview = isEmptyRtValue(area.hint?.text)
  const isHoverMode = !!area.hint?.onHover
  const [showHint, setShowHint] = useState(
    (isPreview && !isSkeleton && !isHoverMode && !hintEmptyPreview) || isDemoMode,
  )
  const showArea = value.showArea && (area.showClickableArea ?? true)

  const [isVisibleItems, setIsVisibleItems] = useState<boolean>(false)
  const [visibleDelayed, setVisibleDelayed] = useState<boolean>(false)
  const { x, y, width, height, id, itemId } = area
  const areaAnimate = isPreview && !(showHighlight || value.showArea) ? 'hide' : 'show'
  const hintVisibleByHover = isPreview && isHoverMode && isHovered && !hintEmptyPreview
  const hintVisibleByShowHint = isPreview ? showHint : selected && showHint
  const hintVisible = !isMobile && (hintVisibleByHover || hintVisibleByShowHint)

  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: blockUuid,
      elementType: CONFIG_ELEMENTS.simulator.type,
      elementId,
    },
  )

  const descriptionId = `simulator-area-description-${id}`
  const areaDescription =
    rtValueToText(area.hint?.text) ||
    (isPreview
      ? `${itemId ? ' linked to ' + value.items.find((item) => item.value == itemId)?.label : ''}`
      : undefined)

  const onMouseDownWrapper = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isFill) {
      setIsDragging(true)
      setShowHint(false)
      onMouseDown(e)
    }
  }

  const onMouseUp = () => {
    setIsDragging(false)
  }

  const onToggleHint = () => {
    setShowHint((prev) => !prev)
  }

  const onDelete = useCallback(() => {
    onDeleteArea(id)
  }, [id, onDeleteArea])

  const handleAreaClick = () => {
    if (isPreview && !validateFields()) {
      return
    }

    const itemIndex = value.items.findIndex((i) => i.value === area.itemId)
    const fromItemIndex = value.items.findIndex((i) => i.value === state?.active)
    const activeItem = value.items[fromItemIndex]
    const activeItemIndex = fromItemIndex < 0 ? 0 : fromItemIndex
    const data = entityAnalyticsConfig.interacted({
      resultExtensions: {
        toSlideId: itemId || null,
        toSlideIndex: itemIndex !== -1 ? itemIndex : null,
        fromSlideId: activeItem?.value,
        fromSlideIndex: activeItemIndex,
      },
      objectExtensions: { ...baseObjectExtensions, itemId: id },
      elementId,
    })
    sendXapiData(data)
    if (isPreview && area.itemId) {
      setState?.({ active: area.itemId || null })
    }
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    if (isPreview && area.rightClickToAction) {
      e.preventDefault()
      handleAreaClick()
    }
  }

  const handleCloseHint = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setShowHint(false)

    areaRef.current?.focus()
  }

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const activeElement = document.activeElement as HTMLElement | null
      const tag = activeElement?.tagName
      const isContentEditable = activeElement?.isContentEditable
      const isInputFocused = tag === 'INPUT' || tag === 'TEXTAREA' || isContentEditable

      const hasTextSelection = () => {
        const selection = window.getSelection()
        return selection && !selection.isCollapsed
      }

      if (
        selected &&
        (e.key === 'Delete' || e.key === 'Backspace') &&
        !isInputFocused &&
        !hasTextSelection()
      ) {
        e.preventDefault()
        onDelete()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [selected, onDelete, enableDelete])

  useEffect(() => {
    if (isVisibleItems) {
      setShowHint(false)
    }
  }, [isVisibleItems])

  useEffect(() => {
    let timeoutId = null

    if (hintVisible && !isPro) {
      timeoutId = setTimeout(() => setVisibleDelayed(true), 300)
    } else {
      setVisibleDelayed(false)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [hintVisible, isPro])

  return (
    <AreaContainerStyled
      animate={areaAnimate}
      aria-describedby={descriptionId}
      aria-label={area?.name || 'Interactive area'}
      initial='hide'
      onContextMenu={isPreview && area.rightClickToAction ? handleContextMenu : undefined}
      onKeyDown={
        isPreview
          ? (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleAreaClick()
              }
            }
          : undefined
      }
      onMouseDown={
        isPreview ? (area.rightClickToAction ? undefined : handleAreaClick) : onMouseDownWrapper
      }
      onMouseEnter={isPreview && isHoverMode ? () => setIsHovered(true) : undefined}
      onMouseLeave={isPreview && isHoverMode ? () => setIsHovered(false) : undefined}
      onMouseUp={onMouseUp}
      ref={areaRef}
      role='button'
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: `${width}%`,
        height: `${height}%`,
      }}
      tabIndex={isPreview ? 0 : undefined}
      variants={ANIMATIONS}
      data-simulator-area
    >
      <DropdownCourse
        container={container || document.body}
        initial='hidden'
        name='simulatorAreaHint'
        offsets={{ x: -2, y: 8 }}
        overlay={<HintContent area={area} id={area.id} onClose={handleCloseHint} />}
        overlayStyle={{
          borderRadius: 'var(--brand-borderRadius)',
          overflow: 'visible',
          backgroundColor: styles.hint?.backgroundColor,
          zIndex: isDemoMode ? 100000 : 1,
        }}
        placement={area.hint?.position || 'bottom-start'}
        styleType='clear'
        visible={!isMobile && visibleDelayed}
      >
        <>
          <AreaStyled
            $disabled={!isFill && !isPreview}
            $hidden={isPreview && !showArea}
            $highlight={showHighlight}
            $isDragging={isDragging}
            $isPreview={isPreview}
            $linked={!!itemId}
            $selected={selected}
            $styles={styles}
          >
            {children}
            {!hintEmptyPreview && isFill && (
              <Tooltip overlay={t('elements.simulator.hintTooltip')}>
                <div className={s.linkedIcon}>
                  <LinkedIcon />
                </div>
              </Tooltip>
            )}
          </AreaStyled>
          {selected && isFill && (
            <AbsolutePortal
              name='simulatorOptions'
              offset='-44px 0'
              placement='topLeft'
              recalculatingKey={[area.x, area.y, area.width, area.height].join('-')}
              zIndex={1}
            >
              <div className={s.toolbars}>
                <AreaToolbar
                  area={area}
                  currentItemId={currentItemId}
                  isVisibleItems={isVisibleItems}
                  itemId={itemId}
                  onDeleteArea={onDelete}
                  setIsVisibleItems={setIsVisibleItems}
                />
                <HintToolbar
                  area={area}
                  onClose={handleCloseHint}
                  onToggleHint={onToggleHint}
                  showHint={showHint}
                />
              </div>
            </AbsolutePortal>
          )}
        </>
      </DropdownCourse>
      <ScreenReaderOnlyText id={descriptionId}>{areaDescription}</ScreenReaderOnlyText>
    </AreaContainerStyled>
  )
}

export default Area
